window.Vendallion = window.Vendallion || {};
Vendallion.FormsHandler = Vendallion.FormsHandler || {};

// Registries
Vendallion.FormsHandler._configs = Vendallion.FormsHandler._configs || new WeakMap();
Vendallion.FormsHandler._groups = Vendallion.FormsHandler._groups || new WeakMap(); // per-form [{selector, validationFor, message}]
Vendallion.FormsHandler._groupState = Vendallion.FormsHandler._groupState || new WeakMap(); // per-form { triedSubmit }

/* ===========================
   Group rule: at least one
   =========================== */

// --- helper: check if a name belongs to a group rule ---
function nameCoveredByAtLeastOneRule(form, name) {
    const groups = Vendallion.FormsHandler._groups.get(form) || [];
    for (const rule of groups) {
        if (form.querySelector(`${rule.selector}[name="${CSS && CSS.escape ? CSS.escape(name) : name}"]`)) {
            return true;
        }
    }
    return false;
}

// --- helper: find all group rules that include a given name ---
function groupsForName(form, name) {
    const groups = Vendallion.FormsHandler._groups.get(form) || [];
    const safe = (s) => (CSS && CSS.escape ? CSS.escape(s) : s);
    return groups.filter((rule) => !!form.querySelector(`${rule.selector}[name="${safe(name)}"]`));
}

function isPhoneField(field, name) {
    const normalizedName = (name || field?.name || "").toLowerCase();
    return field?.type === "tel" || normalizedName.includes("phone") || normalizedName.includes("mobile");
}

function getIntlTelInstance(field) {
    if (!field || typeof window.intlTelInput === "undefined") {
        return null;
    }

    if (typeof window.intlTelInput.getInstance === "function") {
        return window.intlTelInput.getInstance(field);
    }

    return field.iti || null;
}

Vendallion.FormsHandler.RequireAtLeastOne = function (formOrId, options) {
    const form = typeof formOrId === "string" ? document.getElementById(formOrId) : formOrId;
    if (!form) return console.warn("Form not found");

    const rule = {
        selector: options.selector,
        validationFor: options.validationFor,
        message: options.message || null,
    };
    if (!rule.selector || !rule.validationFor) return console.warn("RequireAtLeastOne: missing selector or validationFor.");

    const groups = Vendallion.FormsHandler._groups.get(form) || [];
    groups.push(rule);
    Vendallion.FormsHandler._groups.set(form, groups);

    if (!Vendallion.FormsHandler._groupState.get(form)) {
        Vendallion.FormsHandler._groupState.set(form, { triedSubmit: false });
    }
    const state = Vendallion.FormsHandler._groupState.get(form);

    // Bind live listeners (paint only after first submit)
    const inputs = form.querySelectorAll(rule.selector);
    inputs.forEach((cb) => {
        cb.addEventListener("change", () => {
            validateAtLeastOneGroup(form, rule, { paint: state.triedSubmit });
        });
    });

    // neutralize per-field required flags (so group controls it)
    const cfg = Vendallion.FormsHandler._configs.get(form);
    if (cfg) {
        inputs.forEach((cb) => {
            if (cb.name && cfg[cb.name]) {
                cfg[cb.name].required = false;
            }
        });
    }
};

// Validate one group; optionally paint
function validateAtLeastOneGroup(form, rule, opts = { paint: true }) {
    const span = form.querySelector(`.validation[data-validation-for="${rule.validationFor}"]`);
    const msg = rule.message || span?.getAttribute("data-validation-message") || "Please select at least one option.";

    const boxes = Array.from(form.querySelectorAll(rule.selector));
    const ok = boxes.some((cb) => cb && cb.checked);

    if (opts.paint && span) span.textContent = ok ? "" : msg;
    return ok;
}

/* ===========================
   Core forms handler
   =========================== */

Vendallion.FormsHandler.Init = (formId) => {
    if (formId) {
        const form = document.getElementById(formId);
        if (!form) return console.error(`Form with ID ${formId} not found.`);
        handleForm(form);
        autoDiscoverGroupRules(form);
    } else {
        document.querySelectorAll('[data-vd-component="form"]').forEach((form) => {
            handleForm(form);
            autoDiscoverGroupRules(form);
        });
    }

    function handleForm(form) {
        let formStarted = false;
        const loader = form.querySelector(".loader");

        const validationPatterns = {
            email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            phone: /^[0-9+\-() ]{10,}$/,
        };

        const formFields = form.querySelectorAll("input, textarea, select");
        if (formFields.length === 0) return;

        const fieldsConfig = {};
        formFields.forEach((field) => {
            const name = field.name;
            if (!name) return;
            const normalizedName = name.toLowerCase();

            fieldsConfig[name] = fieldsConfig[name] || {};
            const required = field.getAttribute("data-required") === "true";
            if (required) fieldsConfig[name].required = true;

            const isEmailField = field.type === "email" || normalizedName === "email";
            if (isEmailField) fieldsConfig[name].pattern = validationPatterns.email;
            if (isPhoneField(field, name)) {
                fieldsConfig[name].pattern = validationPatterns.phone;
                fieldsConfig[name].phone = true;
            }

            const span = form.querySelector(`.validation[data-validation-for="${name}"]`);
            fieldsConfig[name].message = span ? span.getAttribute("data-validation-message") : "";
        });
        const infoSpans = form.querySelectorAll(".info-icon");
        infoSpans.forEach((span) => {
            span.addEventListener("mouseover", () => {
                const tooltip = span.parentElement.querySelector(".tooltip");
                if (tooltip) {
                    tooltip.classList.remove("hidden");
                }
            });
            span.addEventListener("mouseout", () => {
                const tooltip = span.parentElement.querySelector(".tooltip");
                if (tooltip) {
                    tooltip.classList.add("hidden");
                }
            });
        });
        Object.keys(fieldsConfig).forEach((n) => (fieldsConfig[n]._originalRequired = !!fieldsConfig[n].required));
        Vendallion.FormsHandler._configs.set(form, fieldsConfig);
        if (!Vendallion.FormsHandler._groupState.get(form)) {
            Vendallion.FormsHandler._groupState.set(form, { triedSubmit: false });
        }

        form.addEventListener("submit", (e) => {
            e.preventDefault();
            clearAllValidation();
            const state = Vendallion.FormsHandler._groupState.get(form);
            state.triedSubmit = true;

            if (validateForm({ paintGroup: true })) submitForm();
        });

        function submitForm() {
            if (loader) loader.classList.replace("hidden", "flex");

            normalizePhoneFields();

            const formData = new FormData(form);
            const captchaValue = form.querySelector(".g-recaptcha-response")?.value;
            const headers = new Headers();
            if (captchaValue) headers.append("g-recaptcha-response", captchaValue);

            fetch(form.action, { method: form.method, body: formData, headers })
                .then((r) => {
                    if (r.ok) {
                        showFormResult("success");
                        form.reset();
                        clearAllValidation();
                        Vendallion.FormsHandler._groupState.get(form).triedSubmit = false;
                        Vendallion.Events?.Emit?.("formSubmit", { formId: form.id });
                    } else showFormResult("error");
                })
                .catch((e) => {
                    console.error("Error submitting form:", e);
                    showFormResult("error");
                });
        }

        function showFormResult(type) {
            if (Vendallion.PopupModal?.Close) Vendallion.PopupModal.Close();
            if (loader) loader.classList.replace("flex", "hidden");

            const tpl = form.getAttribute("data-notification-template");
            if (!tpl) return console.error("Missing data-notification-template");
            const selector = type === "success" ? `#${tpl}-success` : `#${tpl}-error`;
            Vendallion.PopupModal({
                moveContent: selector,
                onOpen: (popup) => popup.querySelector(".close-btn")?.addEventListener("click", Vendallion.PopupModal.Close),
            });
        }

        // Field-level listeners
        Object.keys(fieldsConfig).forEach((key) => {
            const field = form.elements[key];
            if (!field) return;

            if (field instanceof RadioNodeList || (field.length && field[0]?.type === "radio")) {
                Array.from(field).forEach((radio) => {
                    radio.addEventListener("change", () => {
                        validateField(radio);
                        if (!formStarted) {
                            formStarted = true;
                            Vendallion.Events?.Emit?.("formStart", { formId: form.id });
                        }
                    });
                });
            } else if (field.type === "checkbox" || field.type === "select-one" || field.type === "select-multiple") {
                field.addEventListener("change", () => {
                    if (field.type === "checkbox" && nameCoveredByAtLeastOneRule(form, field.name)) {
                        updateValidation(field, "");
                        const state = Vendallion.FormsHandler._groupState.get(form) || { triedSubmit: false };
                        const relatedGroups = groupsForName(form, field.name);
                        relatedGroups.forEach((rule) => validateAtLeastOneGroup(form, rule, { paint: state.triedSubmit }));
                    } else {
                        validateField(field);
                    }

                    if (!formStarted) {
                        formStarted = true;
                        Vendallion.Events?.Emit?.("formStart", { formId: form.id });
                    }
                });
            } else {
                field.addEventListener("focus", () => updateValidation(field, ""));
                field.addEventListener("blur", () => validateField(field));
            }
        });

        function validateForm({ paintGroup } = { paintGroup: false }) {
            let isValid = true;

            Object.keys(fieldsConfig).forEach((key) => {
                if (nameCoveredByAtLeastOneRule(form, key)) return;
                const field = form.elements[key];
                if (field && !validateField(field)) isValid = false;
            });

            const groups = Vendallion.FormsHandler._groups.get(form) || [];
            groups.forEach((rule) => {
                if (!validateAtLeastOneGroup(form, rule, { paint: !!paintGroup })) isValid = false;
            });

            if (form.querySelector(".g-recaptcha") && !validateCaptcha()) isValid = false;
            return isValid;
        }

        function clearAllValidation() {
            Object.keys(fieldsConfig).forEach((key) => {
                const f = form.elements[key];
                if (f) updateValidation(f, "");
            });
            const groups = Vendallion.FormsHandler._groups.get(form) || [];
            groups.forEach((rule) => {
                const span = form.querySelector(`.validation[data-validation-for="${rule.validationFor}"]`);
                if (span) span.textContent = "";
            });
        }

        function updateValidation(field, msg) {
            const span = form.querySelector(`.validation[data-validation-for="${field.name}"]`);
            if (span) span.textContent = msg || "";
        }

        function normalizePhoneFields() {
            Object.keys(fieldsConfig).forEach((key) => {
                if (!fieldsConfig[key]?.phone) {
                    return;
                }

                const field = form.elements[key];
                if (!field || Array.isArray(field)) {
                    return;
                }

                const iti = getIntlTelInstance(field);
                if (!iti || typeof iti.isValidNumber !== "function" || !iti.isValidNumber()) {
                    return;
                }

                const normalizedValue = iti.getNumber();
                if (normalizedValue) {
                    field.value = normalizedValue;
                }
            });
        }

        function validatePhoneField(field, value, message) {
            const iti = getIntlTelInstance(field);
            if (iti && typeof iti.isValidNumber === "function") {
                const selectedCountry = iti.getSelectedCountryData?.();
                const dialCodeLength = selectedCountry?.dialCode?.length || 0;
                const digitsOnly = value.replace(/\D/g, "");

                if (digitsOnly.length <= dialCodeLength) {
                    return { valid: false, msg: message };
                }

                if (!iti.isValidNumber()) {
                    return { valid: false, msg: message };
                }

                return { valid: true, msg: "" };
            }

            if (!validationPatterns.phone.test(value)) {
                return { valid: false, msg: message };
            }

            return { valid: true, msg: "" };
        }

        function validateField(field) {
            let list = null;
            if (typeof RadioNodeList !== "undefined" && field instanceof RadioNodeList) {
                list = Array.from(field);
                if (list.length && nameCoveredByAtLeastOneRule(form, list[0].name)) return true;
            }
            const name = list ? list[0]?.name : field.name;
            const rules = fieldsConfig[name];
            if (!rules) return true;

            let valid = true;
            let msg = "";
            let value;

            if (list && list.length && list[0].type === "checkbox") {
                value = list.some((el) => el.checked);
            } else if (field.type === "checkbox") {
                value = field.checked;
            } else {
                value = (field.value ?? "").trim();
            }

            if (rules.required && (value === "" || value === false)) {
                valid = false;
                msg = rules.message;
            } else if (rules.phone && typeof value === "string" && value) {
                const phoneValidation = validatePhoneField(field, value, rules.message);
                valid = phoneValidation.valid;
                msg = phoneValidation.msg;
            } else if (rules.pattern && typeof value === "string" && value && !rules.pattern.test(value)) {
                valid = false;
                msg = rules.message;
            }

            updateValidation(field, valid ? "" : msg);
            return valid;
        }

        function validateCaptcha() {
            const span = form.querySelector('.validation[data-validation-for="recaptcha"]');
            const value = form.querySelector(".g-recaptcha-response")?.value;
            if (!span) return true;

            // always ensure the span is visible when validating
            span.style.display = "";

            if (!value) {
                span.textContent = span.getAttribute("data-validation-message");
                // removed auto-hide: keep the error visible until user solves reCAPTCHA
                return false;
            }
            span.textContent = "";
            return true;
        }
    }
};

// Auto-discovery: find spans with data-atleast-one and register rules
function autoDiscoverGroupRules(form) {
    form.querySelectorAll(".validation[data-atleast-one]").forEach((span) => {
        const selector = span.getAttribute("data-atleast-one");
        const validationFor = span.getAttribute("data-validation-for");
        const message = span.getAttribute("data-validation-message") || undefined;
        if (!selector || !validationFor) return;
        Vendallion.FormsHandler.RequireAtLeastOne(form, { selector, validationFor, message });
    });
}

Vendallion.FormsHandler.Init();

Vendallion.FormsHandler.ClearFieldValidation = function (formOrId, fieldNameOrElement) {
    const form = typeof formOrId === "string" ? document.getElementById(formOrId) : formOrId;
    if (!form) {
        console.warn("Form not found");
        return;
    }

    let formField = fieldNameOrElement;
    if (typeof fieldNameOrElement === "string") {
        formField = form.elements[fieldNameOrElement];
        if (!formField) {
            console.warn(`Field with name "${fieldNameOrElement}" not found in form ${form.id}`);
            return;
        }
    }
    const name = formField.name;
    if (!name) return;

    const span = form.querySelector(`.validation[data-validation-for="${name}"]`);
    if (span) span.textContent = "";
};

Vendallion.FormsHandler.SetOptional = function (formOrId, fieldNames) {
    const form = typeof formOrId === "string" ? document.getElementById(formOrId) : formOrId;
    if (!form) {
        console.warn("Form not found");
        return;
    }

    const cfg = Vendallion.FormsHandler._configs.get(form);
    if (!cfg) {
        console.warn("Form has not been initialized by FormsHandler.Init");
        return;
    }

    fieldNames.forEach((name) => {
        if (!cfg[name]) return;
        cfg[name].required = false;
        const el = form.elements[name];
        if (el) el.setAttribute("data-required", "false");
        Vendallion.FormsHandler.ClearFieldValidation(form, name);
    });
};

Vendallion.FormsHandler.SetRequired = function (formOrId, fieldNames) {
    const form = typeof formOrId === "string" ? document.getElementById(formOrId) : formOrId;
    if (!form) {
        console.warn("Form not found");
        return;
    }

    const cfg = Vendallion.FormsHandler._configs.get(form);
    if (!cfg) {
        console.warn("Form has not been initialized by FormsHandler.Init");
        return;
    }

    fieldNames.forEach((name) => {
        if (!cfg[name]) return;
        cfg[name].required = true;
        const el = form.elements[name];
        if (el) el.setAttribute("data-required", "true");
        if (el && typeof el.dispatchEvent === "function") {
            el.dispatchEvent(new Event("blur"));
        }
    });
};

Vendallion.FormsHandler.ResetRequirements = function (formOrId) {
    const form = typeof formOrId === "string" ? document.getElementById(formOrId) : formOrId;
    if (!form) {
        console.warn("Form not found");
        return;
    }

    const cfg = Vendallion.FormsHandler._configs.get(form);
    if (!cfg) {
        console.warn("Form has not been initialized by FormsHandler.Init");
        return;
    }

    Object.keys(cfg).forEach((name) => {
        const original = cfg[name]._originalRequired;
        if (typeof original !== "undefined") {
            cfg[name].required = original;
            const el = form.elements[name];
            if (el) el.setAttribute("data-required", original ? "true" : "false");
        }
    });
};
